'use client';

import React from 'react';
import { Eye, EyeOff, Copy, Trash2 } from 'lucide-react';
import { useApiToken } from '@/app/api-docs/context/apiTokenProvider';
import useRoute from '@/ueshook/useRoute';
import Link from 'next/link';
import { Separator } from '@/components/ui/separator';

interface ApiManagementProps {
  onClose?: () => void;
}

export default function ApiManagement({ onClose }: ApiManagementProps) {
  const {
    apiToken,
    showApiToken,
    apiTokenUsage,
    loadingApiTokenUsage,
    createApiToken,
    deleteApiToken,
    copyToken,
    showHideApiToken,
  } = useApiToken();

  const { push } = useRoute();

  // 格式化时间
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  // 模拟的token创建时间和最后使用时间
  const tokenInfo = {
    createdDate: '2/21/2025',
    lastUse: '—',
  };

  const handleBuyApiCredits = () => {
    push('/api-docs/pricing');
    onClose?.();
  };

  const handleViewApiDocs = () => {
    push('/api-docs');
    onClose?.();
  };

  return (
    <div className='flex flex-col gap-6'>
      {/* API Credits Section */}
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
          <div className='mr-4 flex-1'>
            <h2 className='mb-4 text-lg font-semibold'>
              API credits remaining:{' '}
              {loadingApiTokenUsage ? 'Loading...' : apiTokenUsage.remaining}
            </h2>

            {/* Progress Bar */}
            {!loadingApiTokenUsage && apiTokenUsage.has_token && (
              <div className='flex flex-col gap-2'>
                <div className='h-2 w-full rounded-full bg-gray-200'>
                  <div
                    className='h-2 rounded-full bg-[#6137E0] transition-all duration-300'
                    style={{
                      width: `${(apiTokenUsage.usage / apiTokenUsage.limit) * 100}%`,
                    }}
                  ></div>
                </div>
                <p className='text-sm text-gray-600'>
                  Used: {apiTokenUsage.usage}/{apiTokenUsage.limit}
                </p>
              </div>
            )}
          </div>

          {/* Desktop Buy API credits button */}
          <div className='hidden items-center md:flex'>
            <button
              onClick={handleBuyApiCredits}
              className='rounded-md bg-[#6137E0] px-6 py-2 text-sm font-medium text-white hover:bg-[#5129c4]'
            >
              Buy API credits
            </button>
          </div>
        </div>

        {/* Mobile Buy API credits button - Full width */}
        <div className='md:hidden'>
          <button
            onClick={handleBuyApiCredits}
            className='w-full rounded-md bg-[#6137E0] px-6 py-2 text-sm font-medium text-white hover:bg-[#5129c4]'
          >
            Buy API credits
          </button>
        </div>
      </div>

      {/* API Token Section */}
      <div className='flex flex-col gap-4'>
        <h2 className='text-base font-medium'>API Token</h2>

        <div className='rounded-2xl bg-gray-100 p-4'>
          {apiToken ? (
            <div className='space-y-4'>
              {/* Token Display - 统一的表格布局，移动端可左右滑动 */}
              <div className='overflow-x-auto'>
                <table className='w-full min-w-[600px]'>
                  <thead>
                    <tr className='border-b text-left text-sm font-medium'>
                      <th className='pb-2'>Token</th>
                      <th className='pb-2'>Created date</th>
                      <th className='pb-2'>Last use</th>
                      <th className='pb-2'></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className='border-b'>
                      <td className='py-3'>
                        <div className='flex items-center gap-2'>
                          <span className='font-mono text-sm'>
                            {showApiToken
                              ? `${apiToken.slice(0, 8)}${'*'.repeat(12)}${apiToken.slice(-4)}`
                              : '*'.repeat(20)}
                          </span>
                        </div>
                      </td>
                      <td className='py-3 text-sm text-gray-600'>
                        {tokenInfo.createdDate}
                      </td>
                      <td className='py-3 text-sm text-gray-600'>
                        {tokenInfo.lastUse}
                      </td>
                      <td className='py-3'>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={showHideApiToken}
                            className='rounded p-1 hover:bg-gray-100'
                          >
                            {showApiToken ? (
                              <Eye size={16} />
                            ) : (
                              <EyeOff size={16} />
                            )}
                          </button>
                          <button
                            onClick={copyToken}
                            className='rounded p-1 hover:bg-gray-100'
                          >
                            <Copy size={16} />
                          </button>
                          <Separator
                            orientation='vertical'
                            className='mx-1 h-4'
                          />
                          <button
                            onClick={deleteApiToken}
                            className='rounded p-1 hover:bg-gray-100'
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className='rounded-lg border border-gray-200 p-4'>
              <div className='flex flex-col items-center justify-between gap-4 md:flex-row md:gap-0'>
                <div className='text-gray-600'>No API token created yet</div>
                <button
                  onClick={createApiToken}
                  className='w-full rounded-md bg-[#6137E0] px-4 py-2 text-sm font-medium text-white hover:bg-[#5129c4] md:w-auto'
                >
                  Create API token
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* API Documentation Link */}
      <div className='flex flex-col gap-2'>
        <h2 className='text-lg font-semibold'>API Documentation</h2>
        <button
          onClick={handleViewApiDocs}
          className='text-left text-sm text-[#6137E0] underline hover:text-[#5129c4]'
        >
          View API Docs
        </button>
      </div>
    </div>
  );
}
